<template>
    <div
        v-if="showBanner"
        class="session-timer"
        :class="state.expired ? 'session-timer--expired' : 'session-timer--active'"
        role="alert"
    >
        <span class="session-timer__icon">
            <svg v-if="state.expired" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#9B1C1C"/>
                <path d="M12 6.5v6.5" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="16.5" r="1.2" fill="#fff"/>
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#F5A623"/>
                <path d="M12 6.5v6.5" stroke="#1C1C1C" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="16.5" r="1.2" fill="#1C1C1C"/>
            </svg>
        </span>

        <p v-if="state.expired" class="session-timer__text">
            Your payment session has expired. Please refresh the Checkout or return to the merchant.
        </p>
        <p v-else class="session-timer__text">
            You have <b>{{ formattedTime }}</b> to complete this payment
        </p>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { injectStore, mapState } from "redux-vuex";
import { getValueFromPath } from "@/support/helpers";

export default {
    name: "SessionTimer",
    setup() {
        const store = injectStore();
        const checkoutData = ref(store.getState());
        const now = ref(Date.now());
        let tickId = null;

        const subscription = store.subscribe(() => {
            checkoutData.value = store.getState();
        });

        const remainingSeconds = computed(() => {
            const expiresAt = getValueFromPath(checkoutData.value, "session.expiresAt");
            if (!expiresAt) return null;
            const diff = Math.floor((expiresAt - now.value) / 1000);
            return diff > 0 ? diff : 0;
        });

        const formattedTime = computed(() => {
            const total = remainingSeconds.value;
            if (total === null) return "";
            const mins = Math.floor(total / 60);
            const secs = total % 60;
            return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
        });

        const showBanner = computed(() => {
            const currentView = getValueFromPath(checkoutData.value, "view.current");
            if (["SUCCESS_VIEW", "FAILURE_VIEW", "PENDING_VIEW", "TIMEOUT_VIEW"].includes(currentView)) {
                return false;
            }
            const expiresAt = getValueFromPath(checkoutData.value, "session.expiresAt");
            const expired = getValueFromPath(checkoutData.value, "session.expired");
            return !!expiresAt || expired === true;
        });

        onMounted(() => {
            tickId = setInterval(() => {
                now.value = Date.now();
            }, 1000);
        });

        onBeforeUnmount(() => {
            if (tickId) {
                clearInterval(tickId);
                tickId = null;
            }
            subscription();
        });

        return {
            state: mapState({
                expiresAt: (state) => getValueFromPath(state, "session.expiresAt") || null,
                expired: (state) => getValueFromPath(state, "session.expired") || false,
            }),
            formattedTime,
            showBanner,
        };
    },
};
</script>

<style lang="scss" scoped>
.session-timer {
    // Full bleed on mobile; only align to the payment column (container__col-md-8)
    // once the two-column layout actually exists, otherwise the banner is squeezed
    // into 2/3 of an already-narrow screen and the copy wraps mid-sentence.
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
    padding: 1.2rem 1.6rem;
    box-sizing: border-box;

    @media screen and (min-width: $breakpoint-middle * 1px) {
        width: 66.7%;
        gap: 1rem;
        padding: 1.6rem 2.4rem;
    }

    &--full {
        width: 100%;
    }

    &--active {
        background: #FDF6EC;
        color: #1C1C1C;
    }

    &--expired {
        background: #F2C4C4;
        color: #9B1C1C;
    }

    &__icon {
        display: flex;
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;

        @media screen and (min-width: $breakpoint-middle * 1px) {
            width: 2.4rem;
            height: 2.4rem;
        }

        svg {
            width: 100%;
            height: 100%;
        }
    }

    &__text {
        margin: 0;
        font-size: 1.3rem;
        line-height: 1.8rem;
        text-align: center;
        // The countdown must never be clipped or hyphenated away — it is the
        // only signal the user has for how long they have left.
        min-width: 0;
        overflow-wrap: break-word;

        @media screen and (min-width: $breakpoint-middle * 1px) {
            font-size: 1.6rem;
            line-height: 2rem;
        }

        b {
            font-weight: $font-bold;
            white-space: nowrap;
        }
    }
}
</style>